import { Direction, EntityState, EntityType, EntityTypeEnum, InFlightTargetOrganisation, Market, WorkflowActions } from 'api'
import { currentOrganisationIdSelector } from 'selectors/adminSelectors'
import { createCollateralByIdGetterSelector } from 'selectors/collateralSelectors'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { marketDetailGetterSelector } from 'selectors/platformSelectors'
import { productByProductSectorIdGetterSelector } from 'selectors/productSelectors'
import { AppThunk, DateLike } from 'utils'

const inflightTargetGetterSelector = createEntityGetterSelector(EntityType.IN_FLIGHT_TARGET_ORGANISATION)

const update = (payload: { collateralId: string; direction: Direction; target: number }): AppThunk => async (dispatch, getState) => {
  const state = getState()
  const organisationId = currentOrganisationIdSelector(state)
  const collateral = createCollateralByIdGetterSelector(state)(payload.collateralId)
  const product = productByProductSectorIdGetterSelector(state)(collateral?.productSectorId)
  const market = marketDetailGetterSelector(state)(product?.market)
  const marketTradeDate = market?.tradeDate

  if (!organisationId || !product || !marketTradeDate) return

  const getInflightTargetOrganisation = inflightTargetGetterSelector(state)
  const targetInflightFound = getInflightTargetOrganisation(organisationId)

  const targetInflight = targetInflightFound
    ? updateInflightTarget({
        ...payload,
        market: product.market,
        marketTradeDate,
        targetInflightOrganisation: targetInflightFound
      })
    : makeNewInflightTarget({
        ...payload,
        market: product.market,
        marketTradeDate,
        organisationId
      })

  dispatch(
    WorkflowActions.createEntitiesCreateAndSubmitReferenceData(
      [
        {
          data: { ...targetInflight },
          entityState: targetInflightFound ? EntityState.EXISTING : EntityState.NEW,
          entityTypeEnum: EntityTypeEnum.IN_FLIGHT_TARGET_ORGANISATION,
          id: organisationId,
          validationIssues: []
        }
      ],
      []
    )
  )
}

export const InflightTargetThunks = {
  update
}

const makeNewInflightTarget = ({
  organisationId,
  collateralId,
  direction,
  target,
  market,
  marketTradeDate
}: {
  organisationId: string
  collateralId: string
  direction: Direction
  target: number
  market: Market
  marketTradeDate: DateLike
}): InFlightTargetOrganisation => ({
  organisationId,
  targetsByCollateralId: {
    [collateralId]:
      direction === Direction.SELL
        ? { targetSell: target, targetBuy: 0, market, inFlightDate: marketTradeDate }
        : { targetBuy: target, targetSell: 0, market, inFlightDate: marketTradeDate }
  }
})

const updateInflightTarget = ({
  collateralId,
  direction,
  target,
  market,
  marketTradeDate,
  targetInflightOrganisation
}: {
  collateralId: string
  direction: Direction
  target: number
  market: Market
  marketTradeDate: DateLike
  targetInflightOrganisation: InFlightTargetOrganisation
}): InFlightTargetOrganisation => {
  const collateralInflight = targetInflightOrganisation.targetsByCollateralId?.[collateralId]

  const propertyToUpdate = direction === Direction.SELL ? 'targetSell' : 'targetBuy'
  const propertyToNotUpdate = direction === Direction.SELL ? 'targetBuy' : 'targetSell'

  return {
    ...targetInflightOrganisation,
    targetsByCollateralId: {
      ...targetInflightOrganisation.targetsByCollateralId,
      [collateralId]: {
        ...collateralInflight,
        inFlightDate: marketTradeDate,
        market,
        [propertyToUpdate]: target,
        [propertyToNotUpdate]: marketTradeDate === collateralInflight?.inFlightDate ? collateralInflight[propertyToNotUpdate] ?? 0 : 0
      }
    }
  }
}
